/**
 * ContractorGreenlightSend · iter303ak-fix29 — Work In Progress · T30 · Action 10
 * ────────────────────────────────────────────────────────────────────────────
 * MOCKED per-child × per-contractor go-ahead message. Body pre-fills with
 * a status summary:
 *   · Materials received ✓  · Safety erected ✓
 *   · Planned start date    · Weather note
 * Editable inline before send. Stamps
 * `contractor_greenlight_sent_at.{child_id}.{contractor_id}` on the parent.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { sendWorkflowEmail, jobRef, signOff } from './_emailSend';
import { CheckCircle2, ThumbsUp, AlertTriangle, Send, User } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));

function buildDefaultBody(ctx) {
  const start = ctx.plannedStart ? new Date(ctx.plannedStart).toLocaleDateString() : 'to be confirmed';
  const materialsOk = ctx.materialsReceived ? 'Received ✓' : 'Pending';
  const safetyOk = ctx.safetyInspected ? 'Erected & inspected ✓' : 'Pending';
  const city = ctx.city || 'the site';
  return (
    `Hi ${ctx.contractorName || 'team'},\n\n`
    + `Green-light for the job at ${ctx.propertyLine || city}.\n`
    + `• Materials: ${materialsOk}\n`
    + `• Safety: ${safetyOk}\n`
    + `• Planned start: ${start}\n`
    + `• Weather: forecast reviewed for ${city} — please shout if you need anything before start day.\n\n`
    + `Cheers,\nRoofs To Remedy`
  );
}

export default function ContractorGreenlightSend({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const stampFieldPrefix = params.stamp_field_prefix || 'contractor_greenlight_sent_at';

  const property = capturedData?.property || capturedData?.property_record || {};
  const parent = capturedData?.appraisal_record || capturedData?.appraisal || {};

  const ctx = useMemo(() => ({
    plannedStart: parent.work_planned_start_date || capturedData?.work_planned_start_date || '',
    materialsReceived: !!parent.materials_receipt || !!capturedData?.materials_receipt_confirm,
    safetyInspected: !!parent.safety_erection?.inspected_at,
    city: property.suburb || property.city || property.town || '',
    propertyLine: property.full_address || property.street_address || '',
  }), [parent, property, capturedData]);

  const acceptedChildren = useMemo(() => {
    const decisions = parent?.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const [items, setItems] = useState([]);
  const [rowState, setRowState] = useState(() => value?.row_state || {});
  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [globalErr, setGlobalErr] = useState('');

  const loadContractors = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const list = [];
    const nextRow = { ...rowState };
    for (const c of acceptedChildren) {
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        const assignments = overview?.type_assignments || {};
        const seen = new Set();
        for (const cid of Object.values(assignments)) {
          if (!cid || seen.has(cid)) continue;
          seen.add(cid);
          let contractor_name = '';
          let email = '';
          try {
            const { data: cdoc } = await api.get(`/contractors/${cid}`);
            contractor_name = cdoc?.contractor_name || cdoc?.company_name || '';
            email = cdoc?.email || '';
          } catch { /* silent */ }
          const key = `${c.child_id}|${cid}`;
          if (!nextRow[key]) {
            nextRow[key] = {
              email,
              contractor_name,
              body: buildDefaultBody({ ...ctx, contractorName: contractor_name }),
            };
          }
          list.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            contractor_id: cid,
            contractor_name,
          });
        }
      } catch { /* silent */ }
    }
    setItems(list);
    setRowState(nextRow);
    setLoading(false);
  }, [acceptedChildren, ctx]);   

  useEffect(() => { loadContractors(); }, [loadContractors]);

  useEffect(() => {
    if (onChange) onChange({ send_state: sendState, row_state: rowState });
  }, [sendState, rowState]);   

  const updateRow = (key, patch) => setRowState(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const isSent = (key) => !!sendState[key]?.sent_at;

  const sendOne = async (child_id, contractor_id) => {
    const key = `${child_id}|${contractor_id}`;
    const row = rowState[key] || {};
    if (!isEmailValid(row.email)) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Enter a valid email before sending.' } }));
      return;
    }
    setBusyKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    setGlobalErr('');
    try {
      const it = items.find(x => x.child_id === child_id && x.contractor_id === contractor_id) || {};
      await sendWorkflowEmail(api, {
        to: (row.email || '').trim(),
        subject: `Go-ahead – ${jobRef(it)} – Roofs To Remedy Ltd`,
        body: row.body || `Hi ${it.contractor_name || ''},\n\nYou're good to go on ${jobRef(it)}.${signOff}`,
        appraisalId: child_id, event: 'contractor_greenlight',
      });
      const nowIso = new Date().toISOString();
      if (parentAppraisalId) {
        const existing = parent[stampFieldPrefix] || {};
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          [stampFieldPrefix]: {
            ...existing,
            [child_id]: {
              ...(existing[child_id] || {}),
              [contractor_id]: nowIso,
            },
          },
        });
      }
      setSendState(prev => ({
        ...prev,
        [key]: { sent_at: nowIso, ok: true, to_email: (row.email || '').trim(), body: row.body || '' },
      }));
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [key]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusyKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const sendAll = async () => {
    for (const it of items) {
      const key = `${it.child_id}|${it.contractor_id}`;
      if (isSent(key)) continue;
      if (!isEmailValid((rowState[key] || {}).email)) continue;
      await sendOne(it.child_id, it.contractor_id);
    }
  };

  const allSent = items.length > 0 && items.every(i => isSent(`${i.child_id}|${i.contractor_id}`));
  const sentCount = items.filter(i => isSent(`${i.child_id}|${i.contractor_id}`)).length;

  return (
    <div className="space-y-4" data-testid="wf-contractor-greenlight-send">
      <div className="rounded-xl border border-lime-200 bg-lime-50/60 p-4">
        <div className="flex items-start gap-3">
          <ThumbsUp className="w-5 h-5 mt-0.5 shrink-0 text-lime-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-lime-900">Green-Light Each Contractor</div>
            <div className="text-sm mt-1 text-lime-800">
              {params.prompt || 'Send each contractor the go-ahead. Body pre-fills with materials + safety + start-date + weather.'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-lime-200 text-[11px] text-lime-800">
          Sends a real email with the message below.
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading contractors…</div>}

      {!loading && items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No contractors assigned to any accepted child. Assign contractors first, then return here.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600">
              <b className="text-slate-800">{sentCount}</b> of <b className="text-slate-800">{items.length}</b> sent
            </div>
            <button type="button" onClick={sendAll} disabled={allSent || busyKeys.size > 0}
              data-testid="wf-cg-send-all"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-lime-600 hover:bg-lime-700 disabled:opacity-40 inline-flex items-center gap-1.5">
              <Send size={12} /> {allSent ? 'All sent ✓' : (busyKeys.size > 0 ? 'Sending…' : 'Send all')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map(it => {
              const key = `${it.child_id}|${it.contractor_id}`;
              const row = rowState[key] || {};
              const sent = isSent(key);
              const err = sendState[key]?.error;
              const busy = busyKeys.has(key);
              const emailValid = isEmailValid(row.email);
              const label = `${it.quote_type_ref || ''}${it.variant_label ? ` · ${it.variant_label}` : ''} — ${it.contractor_name || it.contractor_id}`;
              return (
                <div key={key} className={`p-3 border rounded-lg ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}
                  data-testid={`wf-cg-row-${it.child_id}-${it.contractor_id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                    {it.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>}
                    {sent && (
                      <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Sent
                      </span>
                    )}
                  </div>
                  <div className="pl-3 border-l-2 border-slate-200 space-y-2">
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Email <span className="text-rose-500">*</span></label>
                      <input type="email" value={row.email || ''}
                        onChange={(e) => updateRow(key, { email: e.target.value })}
                        disabled={sent} placeholder="contractor@email.com"
                        data-testid={`wf-cg-email-${it.child_id}-${it.contractor_id}`}
                        className={`w-full px-2 py-1 text-xs border rounded ${row.email && !emailValid ? 'border-rose-400 bg-rose-50' : 'border-slate-300'} disabled:opacity-60`} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Message body</label>
                      <textarea value={row.body || ''}
                        onChange={(e) => updateRow(key, { body: e.target.value })}
                        disabled={sent}
                        data-testid={`wf-cg-body-${it.child_id}-${it.contractor_id}`}
                        className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded font-mono disabled:opacity-60"
                        rows={6} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] text-slate-500">
                        {sent && <span className="text-emerald-700">✓ Stamped {new Date(sendState[key].sent_at).toLocaleString()} → {sendState[key].to_email}</span>}
                        {err && <span className="text-rose-700">Error: {err}</span>}
                      </div>
                      <button type="button" onClick={() => sendOne(it.child_id, it.contractor_id)}
                        disabled={sent || busy || !emailValid}
                        data-testid={`wf-cg-send-btn-${it.child_id}-${it.contractor_id}`}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-lime-600 hover:bg-lime-700 disabled:opacity-40 inline-flex items-center gap-1.5">
                        <Send size={12} /> {sent ? 'Sent' : (busy ? 'Sending…' : 'Send')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!allSent}
            data-testid="wf-cg-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
